import { Autocomplete, Box, CircularProgress, LinearProgress, Link, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { DeviceHardware, DeviceV3 } from '../../../../../backendsdk';
import { TrackedButton as Button } from '../../../../../components/TrackedComponents';
import { useAlert } from '../../../../../hooks/alert';
import useApi from '../../../../../hooks/api';
import useDevices from '../../../../../hooks/devices';
import useProfile from '../../../../../hooks/profile';
import { normalizeLicensePlate } from '../../../../../utils/Str';
import palette from '../../../../ColorPalette';
import { HORIZONTAL_APERTURE, VERTICAL_APERTURE, WorkerParams } from '../SetupTool';
import ObjectBus from '../assets/feedback/passengers/object.svg';
import ObjectTruck from '../assets/feedback/property/object.svg';
import ImageNA from '../assets/image_not_available.png';
import FixedContainer from '../ui/FixedContainer';
import { AnalysisResult, fallbackValue } from '../utils/analysis';
import getWorker from '../workers/worker';
import { FrontFacingOverlay, INSTRUCTIONS, InCabinOverlay } from './DeviceFeedback';
import { convertParam } from './InstallationParams';

interface VerifyCameraProps {
    malfunctionId?: number;
    installerName: string;
    customerName: string;
    onBack: () => void;
    imei?: string;
    hardware: DeviceHardware;
}

const VerifyCamera: React.FC<VerifyCameraProps> = (props) => {
    const [imei, setImei] = useState<string>(props.imei || '');
    const [selectedDevice, setSelectedDevice] = useState<DeviceV3 | null>(null);
    const [stage, setStage] = useState<number>(0);
    const [snapshot, setSnapshot] = useState<string>('');
    const [isLoadingAction, setIsLoadingAction] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(!!props.imei);
    const [isError, setIsError] = useState<boolean>(false);
    const [workerParams, setWorkerParams] = useState<WorkerParams | undefined>();
    const [horizontalPixel, setHorizontalPixel] = useState<number>();
    const [minDistance, setMinDistance] = useState<number>();
    const [groundPixel, setGroundPixel] = useState<number>();
    const [objectOverlay, setObjectOverlay] = useState<boolean>(false);
    const [enteredDistance, setEnteredDistance] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [imageCounter, setImageCounter] = useState<number>(0);
    const [keyCounter, setKeyCounter] = useState<number>(0);
    const { t } = useTranslation();
    const { api, installApi } = useApi();
    const { profile } = useProfile();
    const history = useHistory();
    const { devices } = useDevices();
    const [alertElement, setAlert] = useAlert();
    const isActionDisabled = !!imei ? isError || !snapshot || isLoadingAction : !selectedDevice;
    const isUS = profile.customer.settings.country === 'us';
    const isMetric = !isUS;
    const units = isMetric ? 'meters' : 'inches';
    const image = profile.customer.settings.driving_type === 'passengers' ? ObjectBus : ObjectTruck;
    const localeMinDistance = !!minDistance
        ? isMetric
            ? minDistance + 0.5
            : convertParam(minDistance + 0.5, 'METRIC', 'IMPERIAL')
        : undefined;
    const isDistanceValid =
        minDistance !== undefined &&
        !!enteredDistance &&
        !isNaN(parseFloat(enteredDistance)) &&
        parseFloat(enteredDistance) >= minDistance;
    const stageCount = objectOverlay ? 4 : 2;

    useEffect(() => {
        if (workerParams) {
            const worker = getWorker();
            worker.onmessage = (event) => {
                const res = event.data as AnalysisResult;
                setHorizontalPixel(res.segmentPixel);
                setMinDistance(res.minDistance);
                setGroundPixel(res.groundPixel);
            };
            worker.onerror = () => {
                setHorizontalPixel(fallbackValue);
            };
            worker.postMessage(workerParams);
            return () => {
                worker.terminate();
            };
        }
    }, [workerParams]);

    const getInstallationParams = () => {
        installApi
            .installParamsDeviceIdGet({ deviceId: imei })
            .then((res) => {
                const installationParams = res.data.installation_params;
                const workerParams = {
                    instHeight: !!installationParams ? parseFloat(installationParams.instHeight) : undefined,
                    instDepth: !!installationParams ? parseFloat(installationParams.instDepth) : undefined,
                    instHoodHeight: !!installationParams ? parseFloat(installationParams.instHoodHeight) : undefined,
                    'vehicle-width': !!installationParams ? parseFloat(installationParams['vehicle-width']) : undefined,
                    horizontalAperture: HORIZONTAL_APERTURE[props.hardware],
                    verticalAperture: VERTICAL_APERTURE[props.hardware],
                };
                if (Object.values(workerParams).some((val) => val === undefined)) {
                    setHorizontalPixel(fallbackValue);
                } else {
                    setWorkerParams(workerParams as WorkerParams);
                }
            })
            .catch(() => setHorizontalPixel(fallbackValue));
    };

    const getSnapshot = () => {
        const now = new Date().getTime() / 1000;
        const since = now - 60;
        api.apiV0DeviceDeviceIdSnapshotGet({ deviceId: imei, timestamp: now, since: since }, { responseType: 'blob' })
            .then((res) => {
                setImageCounter(0);
                setKeyCounter((prev) => prev + 1);
                const reader = new window.FileReader();
                reader.readAsDataURL(res.data);
                reader.onload = () => {
                    const image = reader.result;
                    if (typeof image === 'string') {
                        setSnapshot(image);
                        setIsError(false);
                    }
                };
                reader.onerror = () => {
                    setSnapshot(ImageNA);
                    setIsError(true);
                };
            })
            .catch(() => {
                setImageCounter(0);
                setSnapshot(ImageNA);
                setIsError(true);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (!!imei) {
            getInstallationParams();
            getSnapshot();
        }
    }, [imei]);

    useEffect(() => {
        if (!!imei) {
            if (imageCounter >= 20) {
                // wait for progress bar animation to end
                const timerId = setTimeout(() => {
                    getSnapshot();
                }, 400);
                return () => clearTimeout(timerId);
            } else {
                const timerId = setTimeout(() => {
                    setImageCounter((prev) => prev + 1);
                }, 1000);
                return () => clearTimeout(timerId);
            }
        }
    }, [imei, imageCounter]);

    const convertToBase64 = (url: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            fetch(url)
                .then((response) => response.blob())
                .then((blob) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        resolve(reader.result as string);
                    };
                    reader.onerror = () => {
                        reject();
                    };
                })
                .catch(() => {
                    reject();
                });
        });
    };

    const handleDone = async () => {
        setIsLoadingAction(true);
        try {
            const base64Image = await convertToBase64(snapshot);
            await installApi.installVerifyPost({
                verifyCamerasRequest: {
                    device_id: imei,
                    installer: props.installerName,
                    snapshot: base64Image,
                    malfunction_id: props.malfunctionId,
                },
            });
            history.push(props.malfunctionId !== undefined ? `/tasks/${props.malfunctionId}` : '/tasks');
        } catch {
            setAlert({ message: t('setup_tool.verify_camera.error'), type: 'error', duration: 6000 });
        } finally {
            setIsLoadingAction(false);
        }
    };

    let content;
    if (!imei) {
        content = (
            <Box
                sx={{
                    width: '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Autocomplete
                    fullWidth
                    id="license-plate-field"
                    options={devices.sort((a, b) =>
                        normalizeLicensePlate(a.device.license_plate).localeCompare(
                            normalizeLicensePlate(b.device.license_plate),
                        ),
                    )}
                    value={selectedDevice}
                    onChange={(_e, value) => setSelectedDevice(value)}
                    renderInput={(params) => (
                        <TextField {...params} fullWidth label={t('setup_tool.installation_params.license_plate')} />
                    )}
                    filterOptions={(options, params) => {
                        const filtered = options.filter((option) =>
                            normalizeLicensePlate(option.device.license_plate).includes(
                                normalizeLicensePlate(params.inputValue),
                            ),
                        );
                        return filtered;
                    }}
                    getOptionLabel={(option) => option.device.license_plate || ''}
                    sx={{ mt: 1 }}
                />
            </Box>
        );
    } else if (stage === 0) {
        content = (
            <Box
                sx={{
                    width: '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <LinearProgress
                    key={`${keyCounter}-progress`}
                    variant="determinate"
                    value={(1 - imageCounter / 20) * 100}
                    sx={{ width: '100%', flexShrink: 0 }}
                />
                <FixedContainer style={{ position: 'relative', marginBottom: '8px' }}>
                    <img
                        alt="in cabin image"
                        id="ic-image"
                        data-testid="ic-image"
                        className={`wellness-image ${isError ? 'no-image' : 'ic-image'}`}
                        src={snapshot}
                        style={{ width: '100%', height: '100%' }}
                    />
                    {!isError ? <InCabinOverlay hardware={props.hardware} /> : null}
                </FixedContainer>
                <ul style={{ width: '100%', paddingLeft: '20px' }}>
                    {INSTRUCTIONS.ic.map((item) => (
                        <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                            <Typography>{t(`setup_tool.device_feedback.ic_${item}`)}</Typography>
                        </li>
                    ))}
                </ul>
            </Box>
        );
    } else if (stage === 1 || stage === 3) {
        content = (
            <Box
                sx={{
                    width: '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <LinearProgress
                    key={`${keyCounter}-progress`}
                    variant="determinate"
                    value={(1 - imageCounter / 20) * 100}
                    sx={{ width: '100%', flexShrink: 0 }}
                />
                <FixedContainer style={{ position: 'relative' }}>
                    <img
                        alt="front facing image"
                        id="ff-image"
                        data-testid="ff-image"
                        className={`wellness-image ${isError ? 'no-image' : 'ff-image'}`}
                        src={snapshot}
                        style={{ width: '100%', height: '100%' }}
                    />
                    {!isError ? (
                        <FrontFacingOverlay horizontalPixel={stage === 1 ? horizontalPixel : groundPixel} />
                    ) : null}
                </FixedContainer>
                <ul style={{ width: '100%', paddingLeft: '20px' }}>
                    {stage === 1 ? (
                        INSTRUCTIONS.ff.map((item) => (
                            <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                <Typography>{t(`setup_tool.device_feedback.ff_${item}`)}</Typography>
                            </li>
                        ))
                    ) : (
                        <li>
                            <Typography>{t('setup_tool.device_feedback.object_instructions_2')}</Typography>
                        </li>
                    )}
                </ul>
                {stage === 1 && !!localeMinDistance ? (
                    <Button
                        id="btn-no-horizon"
                        data-testid="btn-no-horizon"
                        variant="contained"
                        color="primary"
                        disabled={!localeMinDistance || isActionDisabled}
                        onClick={() => {
                            setObjectOverlay(true);
                            setStage(2);
                        }}
                    >
                        {t('setup_tool.device_feedback.no_horizon')}
                    </Button>
                ) : null}
            </Box>
        );
    } else if (stage === 2) {
        content = (
            <Box key={stage} sx={{ display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'center' }}>
                <img src={image} alt="object" style={{ width: '100%' }} />
                <Typography sx={{ my: 2 }}>
                    {t('setup_tool.device_feedback.object_instructions_1', { minDistance: localeMinDistance, units })}
                </Typography>
                <TextField
                    fullWidth
                    required
                    label={t('setup_tool.device_feedback.object_distance', {
                        units: t(`setup_tool.installation_params.units.${units}`),
                    })}
                    value={enteredDistance}
                    onChange={(e) => setEnteredDistance(e.target.value)}
                    error={!!errorMessage}
                    helperText={errorMessage}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {alertElement}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: 0,
                }}
            >
                <Typography variant="h6" textAlign="center">
                    {t('setup_tool.verify_camera.header')}
                </Typography>
                <Typography fontSize={14} textAlign="center" sx={{ fontWeight: 500, mb: 1 }}>
                    {t('setup_tool.verify_camera.instructions')}
                </Typography>
                {!!imei && (isLoading || !snapshot) ? (
                    <CircularProgress sx={{ mt: 1 }} />
                ) : (
                    <Box
                        sx={{
                            width: stage === 0 && !imei ? '100%' : undefined,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            overflowY: 'auto',
                            my: 1,
                        }}
                    >
                        {content}
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    minHeight: 0,
                    flexShrink: 0,
                    py: 2,
                }}
            >
                {stage === stageCount - 1 ? (
                    <Link
                        id="adjust-params-link"
                        data-testid="adjust-params-link"
                        component="button"
                        disabled={isActionDisabled}
                        onClick={() => {
                            history.push(
                                props.malfunctionId !== undefined
                                    ? `/tasks/${props.malfunctionId}/adjust`
                                    : '/tasks/adjust',
                            );
                        }}
                        sx={{ fontSize: 14, mb: 1 }}
                    >
                        {t('setup_tool.select_mode.adjust_header')}
                    </Link>
                ) : null}
                <Box sx={{ width: '100%', display: 'flex' }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                        <Button
                            id="btn-back"
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setStage((prev) => {
                                    if (prev > 0) {
                                        setErrorMessage('');
                                        if (prev === 2) {
                                            setObjectOverlay(false);
                                        }
                                        return prev - 1;
                                    }
                                    if (props.imei !== undefined) {
                                        props.onBack();
                                    } else {
                                        if (imei) {
                                            setImei('');
                                        } else {
                                            props.onBack();
                                        }
                                    }
                                    return prev;
                                });
                            }}
                        >
                            {t('setup_tool.back')}
                        </Button>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Button
                            id="btn-next"
                            data-testid="btn-next"
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                if (!imei && !!selectedDevice) {
                                    setImei(selectedDevice.device.device_id);
                                    setIsLoading(true);
                                } else {
                                    if (stage < stageCount - 1) {
                                        setStage((prev) => {
                                            if (prev === 2) {
                                                if (isDistanceValid) {
                                                    setWorkerParams((prev) => {
                                                        if (!prev) {
                                                            return prev;
                                                        }
                                                        return {
                                                            ...prev,
                                                            distance: isMetric
                                                                ? parseFloat(enteredDistance)
                                                                : convertParam(
                                                                      parseFloat(enteredDistance),
                                                                      'METRIC',
                                                                      'IMPERIAL',
                                                                  ),
                                                        };
                                                    });
                                                    return prev + 1;
                                                } else {
                                                    setErrorMessage(
                                                        t('setup_tool.device_feedback.object_distance_error'),
                                                    );
                                                    return prev;
                                                }
                                            }
                                            return prev + 1;
                                        });
                                    } else {
                                        handleDone();
                                    }
                                }
                            }}
                            disabled={isActionDisabled}
                        >
                            {stage < stageCount - 1 ? t('setup_tool.next') : t('setup_tool.completed.done')}
                        </Button>
                    </Box>
                </Box>
                {isError ? (
                    <Box sx={{ minHeight: 0, mt: 1 }}>
                        <Link
                            component="button"
                            onClick={() =>
                                history.push(
                                    props.malfunctionId !== undefined ? `/tasks/${props.malfunctionId}` : '/tasks',
                                )
                            }
                        >
                            <Typography fontSize={14} sx={{ color: palette.neutral[700] }}>
                                {t('setup_tool.back_to_dashboard')}
                            </Typography>
                        </Link>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

export default VerifyCamera;
